import { useEffect, useMemo, useState } from 'react'
import type { LeagueSummary } from '@/features/league/types'
import type { UrgentDeadline } from '../types'

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000

// A league still needs a squad when the user hasn't built one yet (the 15-player
// preview is empty) and the competition is not over. Mirrors ActionCenterCard's
// needsSquad predicate so both surfaces agree on what "no escalação" means.
function needsSquad(league: LeagueSummary): boolean {
  return league.squadPreview.length === 0 && league.competitionStatus !== 'FINISHED'
}

export function useUrgentDeadline(leagues: LeagueSummary[]): UrgentDeadline | null {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 10_000)
    return () => clearInterval(id)
  }, [])

  return useMemo(() => {
    // Total leagues still missing a squad — drives the CTA destination.
    const pendingSquadCount = leagues.filter(needsSquad).length

    // Only warn about leagues where the squad is NOT built yet AND the open
    // window closes within 24h. A user who already finalized every squad must
    // not see this banner.
    const candidates = leagues
      .filter(needsSquad)
      .filter(l => l.windowState === 'open' && l.windowChangesAt !== null)
      .map(l => ({ league: l, closesAt: new Date(l.windowChangesAt!) }))
      .filter(({ closesAt }) => {
        const ms = closesAt.getTime() - now.getTime()
        return ms > 0 && ms < TWENTY_FOUR_HOURS_MS
      })
      .sort((a, b) => a.closesAt.getTime() - b.closesAt.getTime())

    const urgent = candidates[0]
    if (!urgent) return null

    const msLeft      = urgent.closesAt.getTime() - now.getTime()
    const hoursLeft   = Math.floor(msLeft / (60 * 60 * 1000))
    const minutesLeft = Math.floor((msLeft % (60 * 60 * 1000)) / (60 * 1000))

    return {
      leagueName:       urgent.league.name,
      leagueId:         urgent.league.id,
      competitionName:  urgent.league.competitionName,
      competitionSport: urgent.league.competitionSport,
      closesAt:         urgent.closesAt,
      hoursLeft,
      minutesLeft,
      pendingSquadCount,
    }
  }, [leagues, now])
}
